import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, takeLeading } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import { datasetsActions } from 'app-components/flux/datasets';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { generatedReportsSelectors } from 'app-components/flux/generated-reports';
import { generatedReportsActions } from 'app-components/flux/generated-reports/generatedReportsSlice';
import { getLogger, httpClientInterface, isDemoMode } from 'app-components/utils/core';

import { cpMetaForResponseAction } from '../core/actionHelpers';
import type { ResourceActionCreator, ResourceFailureActionCreator } from '../core/resourceStoreTypes';
import { undoActions, undoTypes } from '../undo';
import { compareReportConfigs } from './defaultReportConfigurations';
import * as actions from './reportConfigurationsActions';
import { getLastSyncDate, getReportConfigById, getResourcesByIdWithTombstones } from './reportConfigurationsSelectors';
import * as transformers from './reportConfigurationsTransformers';
import * as types from './reportConfigurationsTypes';
import {
  adjustReportConfiguration,
  adjustSegmentByIfInvalid,
  clearConflictingDateRangeFields,
  resolveDisplayChartType,
} from './utils/reportFieldAvailability';

export function* reportConfigurationsDumpUpdater(resources: Array<types.ReportConfigurationProps>): SagaIterator {
  const reportConfigurations = resources.map((resource) => types.mkReportConfiguration(resource));

  // Snapshot current configs before applying the bump so we can detect meaningful changes.
  // Uses the tombstone-aware selector so isDeleted records are visible — without them a
  // delayed pre-deletion bump would resurrect a config we already deleted locally.
  const currentConfigsById = yield select(getResourcesByIdWithTombstones);

  // Guard against delayed bumps overwriting newer local state.
  const updatedConfigurations = reportConfigurations.filter((incoming) => {
    if (!incoming.id) return true;
    const current = currentConfigsById.get(incoming.id);
    if (!current) return true;
    // Tombstones are permanent: never let a non-deleted bump revive a deleted config.
    if (current.isDeleted && !incoming.isDeleted) return false;
    // Always accept a delete bump for a currently-live config. The server sends
    // dbVersion=0 on deletes, which would otherwise fail the version check below.
    if (incoming.isDeleted && !current.isDeleted) return true;
    if (incoming.dbVersion == null || current.dbVersion == null) return true;
    return incoming.dbVersion > current.dbVersion;
  });

  if (updatedConfigurations.length === 0) return;

  yield put(
    actions.getReportConfigurationsSuccess(
      { resources: updatedConfigurations, doMerge: false },
      { context: 'BUMP_CHANGE_DUMP' },
    ),
  );

  // Re-generate the report if the actively-viewed config changed meaningfully.
  // On the originating client the store already has the updated config (from the CRUD success
  // handler) so compareReportConfigs returns true and no second generate occurs.
  const activeViewingConfigId: string | null = yield select(generatedReportsSelectors.getActiveViewingConfigId);

  for (const config of updatedConfigurations) {
    if (config.isDeleted || !config.id) continue;
    if (config.id !== activeViewingConfigId) continue;

    const existing = currentConfigsById.get(config.id);
    if (!existing) continue;

    if (!compareReportConfigs(existing, config)) {
      yield put(
        generatedReportsActions.generateReport({
          location: config.id,
          configuration: config,
        }),
      );
    }
  }
}

export function* reportConfigurationsNudgeUpdater(): SagaIterator {
  const bumpReportConfigurations = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpReportConfigurations');
  if (bumpReportConfigurations) {
    yield put(actions.getReportConfigurations(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

const mkQcsSyncReportConfigurationsConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<types.ReportConfigurationProps>();

const mkResourceConfig = (
  successAction: ResourceActionCreator<types.ReportConfigurationProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncReportConfigurationsConfig({
    resourceName: 'report-configuration',
    resourceBaseUrl: 'reports/report-configuration',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformResponseToResources: transformers.transformResponseToReportConfigurations,
    transformResourceToRequestData: transformers.transformReportConfigurationToPersistenceData,
    // transformResourcesToRequestData: transformers.transformReportConfigurationsToRequestData,

    successAction,
    failureAction,
  });

export function* getReportConfigurationsActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.getReportConfigurationsSuccess,
    actions.getReportConfigurationsFailure,
  );
  yield resourceSagaUtils.takeAndDebounce(
    actions.getReportConfigurations,
    resourceSagaUtils.qcsSyncGetResources<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* createReportConfigurationActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.createReportConfigurationSuccess,
    actions.createReportConfigurationFailure,
  );
  yield takeLeading(
    actions.createReportConfiguration.type,
    resourceSagaUtils.qcsSyncCreateResource<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* updateReportConfigurationWithAdjustments(
  action: ReturnType<typeof actions.updateReportConfiguration>,
): SagaIterator {
  const shouldAdjust = action.meta?.props?.adjustReportConfiguration;
  // Even without full adjustment (e.g. undo dispatches), clear date params that conflict
  // with the dateRange — QCS rejects updates carrying stale startOn/endOn/interval fields.
  const adjustedConfiguration = shouldAdjust
    ? adjustReportConfiguration(action.payload, action.meta?.props?.adjustProps)
    : clearConflictingDateRangeFields(action.payload);
  yield put(actions.updateReportConfigurationInternal(adjustedConfiguration, action.meta));
}

export function* updateReportConfigurationActionWatcher() {
  if (!isDemoMode()) {
    // takeEvery so that if two different reports are reconfiged/generated at the same time, both requests still process
    yield takeEvery(actions.updateReportConfiguration.type, updateReportConfigurationWithAdjustments);
  }
}

export function* updateReportConfigurationInternalActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.updateReportConfigurationSuccess,
    actions.updateReportConfigurationFailure,
  );
  // takeEvery so that if two different reports are reconfiged/generated at the same time, both requests still process
  yield takeEvery(
    actions.updateReportConfigurationInternal.type,
    resourceSagaUtils.qcsSyncUpdateResource<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

function* generateReportFromConfigAction(
  action: ReturnType<typeof actions.updateReportConfigurationSuccess | typeof actions.createReportConfigurationSuccess>,
) {
  const { payload, meta } = action;
  if (meta?.props?.generateReport) {
    // Trigger report generation if specified in meta props. On create, we set location to id
    yield put(
      generatedReportsActions.generateReport({
        location: meta?.props?.location ?? payload?.id ?? 'reports',
        configuration: adjustReportConfiguration(action.payload, action.meta?.props?.adjustProps),
      }),
    );
  }
}

export function* reportConfigurationSuccessActionWatcher() {
  yield takeEvery(actions.updateReportConfigurationSuccess.type, generateReportFromConfigAction);
  yield takeEvery(actions.createReportConfigurationSuccess.type, generateReportFromConfigAction);
  yield takeEvery(actions.openReportConfigurationSuccess.type, generateReportFromConfigAction);
}

export function* deleteReportConfigurationActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.deleteReportConfigurationSuccess,
    actions.deleteReportConfigurationFailure,
  );
  yield takeEvery(
    actions.deleteReportConfiguration.type,
    resourceSagaUtils.qcsSyncDeleteResource<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

function* getReportConfigurations() {
  yield put(actions.getReportConfigurations());
}

function* datasetPollSuccessActionWatcher() {
  yield takeEvery(datasetsActions.datasetPollSuccess, getReportConfigurations);
}

const log = getLogger('reportConfigurationsSagas');

function* openReportConfigurationWorker(action: ReturnType<typeof actions.openReportConfiguration>): SagaIterator {
  const { parentConfigId, parentPersistentType } = action.payload;
  try {
    const base = httpClientInterface.getServiceUrl();
    const path = `reports/report-configuration/open/${parentConfigId}`;
    const url = new URL(path, base);
    if (parentPersistentType) {
      url.searchParams.set('parentPersistentType', parentPersistentType);
    }

    const response = yield call(httpClientInterface.send, 'POST', url.href, undefined, undefined, undefined);

    const resourceData = response?.data?.resources?.[0] ?? response?.data;
    if (!resourceData) {
      throw new Error('No config returned from open API');
    }
    const openedConfig = types.mkReportConfiguration(resourceData);
    const config = openedConfig.reportType === 'COMPARISON' ? adjustSegmentByIfInvalid(openedConfig) : openedConfig;

    const meta = {
      ...cpMetaForResponseAction(action.meta),
      props: { generateReport: true, location: config.id },
    };
    yield put(actions.openReportConfigurationSuccess(config, meta));
    action.meta?.resolve?.(config);
  } catch (error: any) {
    log.error('Error opening report configuration', error);
    yield put(
      undoActions.addUndo(
        undoTypes.mkUndo({
          userMessage: 'Sorry, but we were unable to open this report.',
          key: uuidv4(),
        }),
      ),
    );
    yield put(
      actions.openReportConfigurationFailure(
        { message: error?.message },
        cpMetaForResponseAction(action.meta) ?? undefined,
      ),
    );
    action.meta?.reject?.(error);
  }
}

export function* openReportConfigurationActionWatcher() {
  yield takeLeading(actions.openReportConfiguration.type, openReportConfigurationWorker);
}

/**
 * Normalize API responses that may return a single resource or an array,
 * then transform into ReportConfiguration records.
 */
function normalizeAndTransformConfigs(response: any): types.ReportConfiguration[] {
  const resources = response?.data?.resources ?? response?.data?.resource;
  const normalized = {
    ...response,
    data: { resources: Array.isArray(resources) ? resources : resources ? [resources] : [] },
  };
  return transformers.transformResponseToReportConfigurations(normalized) ?? [];
}

function* saveReportConfigurationWorker(action: ReturnType<typeof actions.saveReportConfiguration>): SagaIterator {
  const { id, name, saveAsNew } = action.payload;
  try {
    const currentConfig: types.ReportConfiguration | undefined = yield select(getReportConfigById, { id });

    const base = httpClientInterface.getServiceUrl();
    if (currentConfig?.reportType === 'COMPARISON') {
      const chartType = resolveDisplayChartType(
        currentConfig.reportType,
        currentConfig.reportSubType,
        currentConfig.chartType,
        currentConfig.timestep,
      );
      // Save/Save As clones the server's working copy. Normalize legacy chart types before cloning,
      // even when the user has only visited a drill-down and has not edited the root configuration.
      if (chartType !== currentConfig.chartType) {
        const updateUrl = new URL(`reports/report-configuration/${id}`, base);
        yield call(
          httpClientInterface.send,
          'PUT',
          updateUrl.href,
          transformers.transformReportConfigurationToPersistenceData(currentConfig.set('chartType', chartType)),
          undefined,
          undefined,
        );
      }
    }
    const path = `reports/report-configuration/save/${id}`;
    const url = new URL(path, base);

    const response = yield call(httpClientInterface.send, 'POST', url.href, { name, saveAsNew }, undefined, undefined);

    const configs = normalizeAndTransformConfigs(response);
    if (!configs || configs.length === 0) {
      throw new Error('No configs returned from save API');
    }

    // After saving, ensure the original config (OPEN or TEMPORARY) is linked to
    // the newly created SAVED config. We base the patched config on the SAVED
    // parent's report-setting values so that server-normalized fields (e.g.
    // resolved startOn/endOn, filter structure) match exactly. This ensures
    // useIsConfigModified correctly reports isModified=false after save.
    const savedConfig = configs.find(
      (c) => c.id !== id && (c.persistentType === 'SAVED' || c.persistentType === 'CUSTOM'),
    );
    if (savedConfig) {
      if (currentConfig) {
        // Patch the SAVED config so the sidebar list shows the correct display name.
        // `name` stays as the report type key for lookups; `description` holds the
        // user-entered display name.
        const savedIndex = configs.indexOf(savedConfig);
        const patchedSaved = savedConfig.merge({ description: name });
        if (savedIndex >= 0) {
          configs[savedIndex] = patchedSaved;
        }

        // Start from the SAVED config's field values (so report settings match
        // the parent exactly), then overlay identity/metadata from the original.
        // This ensures useIsConfigModified correctly reports isModified=false.
        const patchedOpen = patchedSaved.merge({
          id: currentConfig.id,
          clientId: currentConfig.clientId,
          persistentType: currentConfig.persistentType,
          location: currentConfig.location,
          parentConfigId: savedConfig.id,
          parentPersistentType: savedConfig.persistentType,
          description: name,
          source: currentConfig.source || 'SAVED_REPORTS',
        });
        configs.push(patchedOpen);
      }
    }

    yield put(actions.saveReportConfigurationSuccess(configs, cpMetaForResponseAction(action.meta) ?? undefined));
    action.meta?.resolve?.(configs);
  } catch (error: any) {
    log.error('Error saving report configuration', error);
    yield put(
      undoActions.addUndo(
        undoTypes.mkUndo({
          userMessage: 'Sorry, but we were unable to save this report.',
          key: uuidv4(),
        }),
      ),
    );
    yield put(
      actions.saveReportConfigurationFailure(
        { message: error?.message },
        cpMetaForResponseAction(action.meta) ?? undefined,
      ),
    );
    action.meta?.reject?.(error);
  }
}

export function* saveReportConfigurationActionWatcher() {
  yield takeLeading(actions.saveReportConfiguration.type, saveReportConfigurationWorker);
}

function* deleteSavedReportConfigurationWorker(
  action: ReturnType<typeof actions.deleteSavedReportConfiguration>,
): SagaIterator {
  const { id } = action.payload;
  try {
    const base = httpClientInterface.getServiceUrl();
    const path = `reports/report-configuration/delete-saved/${id}`;
    const url = new URL(path, base);

    const response = yield call(httpClientInterface.send, 'DELETE', url.href, undefined, undefined, undefined);

    const configs = normalizeAndTransformConfigs(response);
    if (!configs || configs.length === 0) {
      throw new Error('No configs returned from delete-saved API');
    }

    const deletedConfigs = configs.map((c) => c.set('isDeleted', true));

    yield put(
      actions.deleteSavedReportConfigurationSuccess(deletedConfigs, cpMetaForResponseAction(action.meta) ?? undefined),
    );
    action.meta?.resolve?.(deletedConfigs);
  } catch (error: any) {
    log.error('Error deleting saved report configuration', error);
    yield put(
      undoActions.addUndo(
        undoTypes.mkUndo({
          userMessage: 'Sorry, but we were unable to delete this saved report.',
          key: uuidv4(),
        }),
      ),
    );
    yield put(
      actions.deleteSavedReportConfigurationFailure(
        { id, message: error?.message },
        cpMetaForResponseAction(action.meta) ?? undefined,
      ),
    );
    action.meta?.reject?.(error);
  }
}

export function* deleteSavedReportConfigurationActionWatcher() {
  yield takeEvery(actions.deleteSavedReportConfiguration.type, deleteSavedReportConfigurationWorker);
}

function* renameReportConfigurationWorker(action: ReturnType<typeof actions.renameReportConfiguration>): SagaIterator {
  const { id, name } = action.payload;
  try {
    const base = httpClientInterface.getServiceUrl();
    const path = `reports/report-configuration/rename/${id}`;
    const url = new URL(path, base);

    const response = yield call(httpClientInterface.send, 'POST', url.href, { name }, undefined, undefined);

    const configs = normalizeAndTransformConfigs(response);
    if (!configs || configs.length === 0) {
      throw new Error('No configs returned from rename API');
    }

    // Ensure the local description field reflects the new name on returned
    // SAVED/CUSTOM configs so getReportDisplayName picks it up immediately.
    const patchedConfigs = configs.map((c) =>
      c.persistentType === 'SAVED' || c.persistentType === 'CUSTOM' ? c.set('description', name) : c,
    );

    yield put(
      actions.renameReportConfigurationSuccess(patchedConfigs, cpMetaForResponseAction(action.meta) ?? undefined),
    );
    action.meta?.resolve?.(patchedConfigs);
  } catch (error: any) {
    log.error('Error renaming report configuration', error);
    yield put(
      undoActions.addUndo(
        undoTypes.mkUndo({
          userMessage: 'Sorry, but we were unable to rename this report.',
          key: uuidv4(),
        }),
      ),
    );
    yield put(
      actions.renameReportConfigurationFailure(
        { id, message: error?.message },
        cpMetaForResponseAction(action.meta) ?? undefined,
      ),
    );
    action.meta?.reject?.(error);
  }
}

export function* renameReportConfigurationActionWatcher() {
  yield takeLeading(actions.renameReportConfiguration.type, renameReportConfigurationWorker);
}

function* reportConfigurationFailureActionWatcher() {
  yield takeEvery(
    [
      actions.createReportConfigurationFailure.type,
      actions.updateReportConfigurationFailure.type,
      actions.deleteReportConfigurationFailure.type,
      actions.openReportConfigurationFailure.type,
      actions.saveReportConfigurationFailure.type,
      actions.deleteSavedReportConfigurationFailure.type,
      actions.renameReportConfigurationFailure.type,
    ],
    getReportConfigurations,
  );
}

export default [
  getReportConfigurationsActionWatcher,
  createReportConfigurationActionWatcher,
  updateReportConfigurationActionWatcher,
  updateReportConfigurationInternalActionWatcher,
  deleteReportConfigurationActionWatcher,
  openReportConfigurationActionWatcher,
  saveReportConfigurationActionWatcher,
  deleteSavedReportConfigurationActionWatcher,
  renameReportConfigurationActionWatcher,
  reportConfigurationSuccessActionWatcher,
  datasetPollSuccessActionWatcher,
  reportConfigurationFailureActionWatcher,
];
